import {
  LRS_ESIGN_INITIATE_URL,
  LRS_ESIGN_STATUS_URL,
  LRS_FORM_DATA_URL,
  LRS_PREVIEW_URL,
  LRS_URL,
  MOBILE_SIGN_LINK_URL,
  MOBILE_SIGN_STATUS_URL,
  MOBILE_SIGN_UPLOAD_URL,
} from 'constants/config';
import type { PreviousPayment } from 'models/lrs';
import { fetchWithAuthentication } from 'utils/fetchJson/fetchJson';

type SaveFormDataRequestBody = {
  paymentId: string;
  paymentToken: string;
  bankAccountNumber: string;
  studentFullName: string;
  studentID: string;
  transactions: PreviousPayment[];
};

type PaymentIdentity = {
  paymentId: string;
  paymentToken: string;
};

type MobileSignLink = {
  url: string;
  expiresAt: string;
};

type SendMobileLinkPayload = PaymentIdentity & {
  phoneNumber: string;
  channel: 'whatsapp' | 'sms';
};

type PreviewFormRequestBody = {
  paymentId: string;
  paymentToken: string;
  bankAccountNumber?: string;
  studentFullName?: string;
  studentID?: string;
  transactions?: PreviousPayment[];
};

type MobileSignStatus = {
  status: 'pending' | 'opened' | 'signed' | 'timeout';
};

type EsignInitiateResponse = {
  status: 'initiated';
  initiatedAt: string;
  expiresInSeconds: number;
  redirectUrl: string;
};

type AadharEsignStatus = {
  status: 'initiated' | 'signed' | 'failed' | 'timeout' | 'aadhaar_mismatch';
  initiatedAt: string;
  expiresInSeconds: number;
};

export const lrs = {
  fetchMobileSignLink: ({ paymentId, paymentToken }: PaymentIdentity) =>
    fetchWithAuthentication<MobileSignLink>(
      `${MOBILE_SIGN_LINK_URL}?${buildPaymentQuery(paymentId, paymentToken)}`,
      { method: 'GET' },
    ),

  pollMobileSignStatus: ({ paymentId, paymentToken }: PaymentIdentity) =>
    fetchWithAuthentication<MobileSignStatus>(
      `${MOBILE_SIGN_STATUS_URL}?${buildPaymentQuery(paymentId, paymentToken)}`,
      { method: 'GET' },
    ),

  initiateAadharEsign: ({ paymentId, paymentToken }: PaymentIdentity) =>
    fetchWithAuthentication<EsignInitiateResponse>(LRS_ESIGN_INITIATE_URL, {
      method: 'POST',
      body: JSON.stringify({
        payment_id: paymentId,
        payment_token: paymentToken,
      }),
    }),

  pollAadharEsignStatus: ({ paymentId, paymentToken }: PaymentIdentity) =>
    fetchWithAuthentication<AadharEsignStatus>(
      `${LRS_ESIGN_STATUS_URL}?${buildPaymentQuery(paymentId, paymentToken)}`,
      { method: 'GET' },
    ),

  sendMobileLink: (payload: SendMobileLinkPayload) => {
    const body = JSON.stringify({
      payment_id: payload.paymentId,
      payment_token: payload.paymentToken,
      phone_number: payload.phoneNumber,
      channel: payload.channel,
    });

    return fetchWithAuthentication<void>(MOBILE_SIGN_LINK_URL, {
      method: 'POST',
      body,
    });
  },

  saveFormData: (payload: SaveFormDataRequestBody) => {
    const body = JSON.stringify({
      payment_id: payload.paymentId,
      payment_token: payload.paymentToken,
      bank_account_number: payload.bankAccountNumber,
      student_full_name: payload.studentFullName,
      student_id: payload.studentID,
      transactions: parseTransactions(payload.transactions),
    });

    return fetchWithAuthentication<void>(LRS_FORM_DATA_URL, {
      method: 'POST',
      body,
    });
  },

  previewForm: (payload: PreviewFormRequestBody) => {
    const body = JSON.stringify({
      payment_id: payload.paymentId,
      payment_token: payload.paymentToken,
      bank_account_number: payload.bankAccountNumber,
      student_full_name: payload.studentFullName,
      student_id: payload.studentID,
      transactions: parseTransactions(payload.transactions),
    });

    return fetch(LRS_PREVIEW_URL, { method: 'POST', body }).then(
      async response => {
        if (!response.ok) {
          throw new Error(`LRS preview failed: ${response.status}`);
        }
        return response.arrayBuffer();
      },
    );
  },

  validateMobileSignLink: ({
    paymentId,
    token,
  }: {
    paymentId: string;
    token: string;
  }) =>
    fetchWithAuthentication<{
      status: 'opened' | 'signed';
      expires_at: string;
    }>(
      `${MOBILE_SIGN_LINK_URL}/${paymentId}?token=${encodeURIComponent(token)}`,
      { method: 'GET' },
    ),

  submitMobileSign: ({
    paymentId,
    linkToken,
    signatureImage,
  }: {
    paymentId: string;
    linkToken: string;
    signatureImage: string;
  }) =>
    fetchWithAuthentication<void>(MOBILE_SIGN_UPLOAD_URL, {
      method: 'POST',
      body: JSON.stringify({
        payment_id: paymentId,
        link_token: linkToken,
        signature: signatureImage,
      }),
    }),

  uploadDrawnSignature: ({
    paymentId,
    paymentToken,
    signatureImage,
  }: {
    paymentId: string;
    paymentToken: string;
    signatureImage: string;
  }) =>
    fetchWithAuthentication<void>(MOBILE_SIGN_UPLOAD_URL, {
      method: 'POST',
      body: JSON.stringify({
        payment_id: paymentId,
        payment_token: paymentToken,
        signature: signatureImage,
      }),
    }),

  delete: ({
    paymentId,
    paymentToken,
  }: {
    paymentId: string;
    paymentToken: string;
  }) => {
    const options = { method: 'DELETE' };

    const url = `${LRS_URL}/${paymentId}?payment_token=${paymentToken}`;

    return fetch(url, options);
  },
};

const buildPaymentQuery = (paymentId: string, paymentToken: string) =>
  new URLSearchParams({
    payment_id: paymentId,
    payment_token: paymentToken,
  }).toString();

const parseTransactions = (transactions: PreviousPayment[] = []) => {
  if (!transactions.length) {
    return null;
  }

  return transactions.map(({ adBranch, ...transaction }) => ({
    ...transaction,
    address: adBranch,
  }));
};

export type { AadharEsignStatus };
